import { Link } from "react-router-dom";
import { Mic, Sparkles, Languages, BookA, Zap, ArrowRight, Command, Captions, Puzzle, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "@/components/AppShell";
import { LanguagesShowcase } from "@/components/LanguagesShowcase";
import { AnyAppShowcase } from "@/components/AnyAppShowcase";
import { MobileCta } from "@/components/MobileCta";

const features = [
  { icon: Sparkles, t: "Auto-cleanup", d: "Ums, uhs and false starts disappear. Grammar and punctuation fixed as you speak." },
  { icon: Languages, t: "100+ languages", d: "Hindi, English, Hinglish and 100 more. Mix them mid-sentence or translate as you speak." },
  { icon: BookA, t: "Your dictionary", d: "Names, brands and jargon spelled exactly how you want, every time." },
  { icon: Zap, t: "Voice snippets", d: 'Say "my address" and your full address appears. Same for sign-offs and links.' },
  { icon: Command, t: "Command mode", d: 'Select text — in the app or on any web page — and say "make this shorter" or "translate to Hindi". Done.' },
  { icon: Captions, t: "Live captions", d: "Watch your words appear as you speak, then get the polished version." },
  { icon: Puzzle, t: "Chrome extension", d: "Dictate straight into Gmail, WhatsApp Web and any text box on the web." },
];
const perks = ["800 tasks every month", "Yearly plan ₹850 (save ₹170)", "Instant ₹85 refills", "Tone modes & command mode", "Chrome extension", "100+ languages & translation"];

const Pricing = ({ cta }) => (
  <section className="px-6 sm:px-12 pb-28 max-w-6xl" data-testid="landing-pricing">
    <div className="panel p-8 sm:p-10 grid md:grid-cols-[1fr_auto] gap-8 items-center">
      <div>
        <div className="eyebrow mb-3">Pricing</div>
        <div className="font-serif text-5xl text-[var(--ink)]">₹85<span className="text-lg font-sans text-white/45"> / month</span></div>
        <p className="text-[var(--accent)] font-semibold mt-2" data-testid="landing-trial-text">First 5 tasks free · invite a friend for +5</p>
        <ul className="mt-5 grid sm:grid-cols-2 gap-2">
          {perks.map((p) => <li key={p} className="flex items-center gap-2 text-sm text-white/65"><Check size={15} className="text-[var(--accent)]" /> {p}</li>)}
        </ul>
      </div>
      <Link to={cta} data-testid="landing-pricing-cta" className="btn-accent text-base px-6 py-3.5">Try 5 free tasks <ArrowRight size={16} /></Link>
    </div>
  </section>
);
const demoBars = Array.from({ length: 48 }, (_, i) => 20 + Math.abs(Math.sin(i * 0.55) * 70) * (0.4 + ((i * 37) % 10) / 16));

export default function Landing() {
  const { user } = useAuth();
  const cta = user ? "/flow" : "/login";
  return (
    <div className="flow-bg min-h-screen pb-20 sm:pb-0" data-testid="landing-page">
      <MobileCta to={cta} label={user ? "Open workspace" : "Start with 5 free tasks"} />
      <header className="flex items-center justify-between px-5 sm:px-12 py-5 sm:py-6" style={{ paddingTop: "max(1.25rem, env(safe-area-inset-top))" }}>
        <Brand />
        <div className="flex items-center gap-4">
          <Link to="/download" data-testid="landing-nav-keyboard" className="text-xs sm:text-sm text-white/60 hover:text-white transition-colors">Get the app</Link>
          <Link to={cta} data-testid="landing-nav-cta" className="btn-ghost text-sm">{user ? "Open workspace" : "Log in"}</Link>
        </div>
      </header>
      <section className="px-5 sm:px-12 pt-10 sm:pt-24 pb-16 sm:pb-20 max-w-6xl">
        <div className="eyebrow mb-6 fade-up">AI voice dictation</div>
        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.98] text-[var(--ink)] max-w-4xl fade-up" style={{ animationDelay: "80ms" }}>
          Don't type. <em className="text-[var(--accent)]">Just speak.</em><br />It comes out polished.
        </h1>
        <p className="mt-8 text-base sm:text-lg text-white/55 max-w-xl fade-up" style={{ animationDelay: "160ms" }}>
          Mulankan turns your rambling voice notes into clean, ready-to-send writing — emails, messages, notes — 4× faster than typing.
        </p>
        <div className="mt-10 flex flex-wrap items-center gap-4 fade-up" style={{ animationDelay: "240ms" }}>
          <Link to={cta} data-testid="landing-start-button" className="btn-accent text-base px-6 py-3.5"><Mic size={18} /> Start with 5 free tasks <ArrowRight size={16} /></Link>
          <span className="text-sm text-white/40">Works in your browser, or add it to your home screen.</span>
        </div>
      </section>
      <section className="px-6 sm:px-12 pb-24 max-w-6xl">
        <div className="panel p-6 sm:p-10 grid lg:grid-cols-2 gap-10 items-center fade-up" style={{ animationDelay: "320ms" }} data-testid="landing-demo">
          <div>
            <div className="eyebrow mb-3">You say</div>
            <p className="text-white/45 italic">"um so hey priya, uh, the meeting is, like, moved to thursday — no wait, friday at 3, and um send me the deck"</p>
            <div className="flex items-end gap-[3px] h-16 mt-6">
              {demoBars.map((h, i) => <span key={i} className="w-[3px] rounded-full bg-[var(--accent)] wave-bar" style={{ height: `${h}%`, animationDelay: `${i * 40}ms` }} />)}
            </div>
          </div>
          <div className="border-l-0 lg:border-l border-white/10 lg:pl-10">
            <div className="eyebrow mb-3">Mulankan writes</div>
            <p className="text-[var(--ink)] text-lg leading-relaxed">Hey Priya, the meeting has moved to Friday at 3 PM. Please send me the deck.</p>
          </div>
        </div>
      </section>
      <AnyAppShowcase />
      <LanguagesShowcase />
      <section className="px-6 sm:px-12 pb-28 max-w-6xl grid sm:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-12">
        {features.map((f, i) => (
          <div key={f.t} className="fade-up" style={{ animationDelay: `${400 + i * 80}ms` }} data-testid={`landing-feature-${i}`}>
            <f.icon size={22} className="text-[var(--accent)] mb-4" />
            <h3 className="font-serif text-2xl text-[var(--ink)] mb-2">{f.t}</h3>
            <p className="text-sm text-white/50 max-w-sm">{f.d}</p>
          </div>
        ))}
      </section>
      <Pricing cta={cta} />
      <footer className="px-6 sm:px-12 py-8 border-t border-white/[0.06] flex flex-wrap gap-6 text-xs text-white/35">
        <span>© {new Date().getFullYear()} Mulankan by Ishukart</span>
        <Link to="/download" data-testid="landing-footer-keyboard" className="hover:text-white/70">Get the app</Link>
        <Link to="/privacy" className="hover:text-white/70">Privacy</Link>
        <Link to="/terms" className="hover:text-white/70">Terms</Link>
        <Link to="/refund" className="hover:text-white/70">Refunds</Link>
        <Link to="/contact" className="hover:text-white/70">Contact</Link>
      </footer>
    </div>
  );
}
